import { Button, Icon, ScrollArea, token } from "@may-i/mash-design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import type { AnalysisConfig } from "~/entities/analysis-config/types";
import type { CameraMemo } from "~/entities/camera-memo/types";
import type { Camera } from "~/entities/camera/types";
import { isCameraAnalyzed } from "~/entities/camera/utils";
import type { FloorPlan } from "~/entities/floor-plan/types";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { AllMemosPanel } from "~/pages/analysis-draft/AllMemosPanel";
import { TEMP_IMAGE_URL } from "~/shared/ui/EmptyImagePlaceholder";
import { compareCamId } from "~/shared/lib/compare-cam-id";
import { AdjustmentHelperRect } from "./AdjustmentHelperRect";
import { FilterPopover } from "./CameraFilterPopover";
import { MappingCameraCard } from "./MappingCameraCard";
import { type MemoAction, MemoPanel } from "./MemoPanel";


/** 미리보기 기준 비율 — 이 비율보다 가로로 길면 폭을 꽉 채우고, 좁으면 높이를 고정한다 */
const PREVIEW_BASE_RATIO = 16 / 9;
/** 16:9 스냅샷일 때의 기존 미리보기 높이 — 어떤 비율이든 이 높이를 넘지 않게 해서 목록이 밀리지 않는다 */
const PREVIEW_HEIGHT = 250;
/** 극단적으로 세로로 긴 이미지에서 폭이 지나치게 좁아지지 않도록 하한 */
const PREVIEW_MIN_RATIO = 0.5;

function scrollToCard(gridEl: HTMLDivElement | null, cameraId: number) {
  if (!gridEl) return;
  const card = gridEl.querySelector(`[data-camera-id="${cameraId}"]`);
  card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

interface CameraSidebarProps {
  cameras: Camera[];
  cameraMappingTypes: Map<number, "add" | "edit">;
  /** F10: 카메라별 분석설정(라인/구역) — 이벤트 소스 타입 필터용 */
  configByCameraId: Map<number, AnalysisConfig>;
  /** F9: 목록 필터 결과(보이는 카메라 id) 를 부모로 통지 → 도면 캔버스에도 적용. null = 필터 미적용(전체) */
  onVisibleCameraIdsChange?: (ids: Set<number> | null) => void;
  someFloorPlanCameraIds: Set<number>;
  nonAnalysisNumberMap: Map<number, string>;
  selectedCameraId: number | null;
  scrollToBottom?: number;
  memos: CameraMemo[];
  selectedFloorPlanId: number | null;
  selectedMemoId: number | null;
  floorPlans: FloorPlan[];
  localMappings: Map<number, { floorPlanId: number; position: { x: number; y: number; rotation: number }; type: string }>;
  // 외부(예: 마커 툴바 메모 추가)에서 메모 패널 열기 + 즉시 새 메모 작성 모드 진입 요청. 카운터 값이 바뀌면 트리거
  openMemoSignal?: number;
  // 화각 조정 — 선택된 카메라가 조정 모드면 헤더/외곽선 강조 + helper rect 표시
  isAdjustmentActiveForSelected?: boolean;
  selectedCameraAdjustmentFrame?: { dx: number; dy: number } | null;
  onAdjustmentFrameChange?: (cameraId: number, frame: { dx: number; dy: number }) => void;
  /** 화각 프레임 드래그 시작 — 되돌리기 스냅샷용 (onAdjustmentFrameChange 는 드래그 내내 발생) */
  onAdjustmentFrameChangeStart?: () => void;
  onEndAdjustment?: () => void;
  onCancelAdjustment?: (cameraId: number) => void;
  onCameraClick: (camera: Camera) => void;
  onCameraSelect: (cameraId: number) => void;
  onAddTempCamera: () => void;
  onMemoSelect: (memoId: number | null) => void;
  onMemoSave: (actions: MemoAction[]) => void;
  onToggleMemoOnFloorPlan: (memo: CameraMemo) => void;
}

export function CameraSidebar({
  cameras,
  cameraMappingTypes,
  configByCameraId,
  onVisibleCameraIdsChange,
  someFloorPlanCameraIds,
  nonAnalysisNumberMap,
  selectedCameraId,
  scrollToBottom,
  memos,
  selectedFloorPlanId,
  selectedMemoId,
  floorPlans,
  localMappings,
  openMemoSignal,
  isAdjustmentActiveForSelected,
  selectedCameraAdjustmentFrame,
  onAdjustmentFrameChange,
  onAdjustmentFrameChangeStart,
  onEndAdjustment,
  onCancelAdjustment,
  onCameraClick,
  onCameraSelect,
  onAddTempCamera,
  onMemoSelect,
  onMemoSave,
  onToggleMemoOnFloorPlan,
}: CameraSidebarProps) {
  const { t } = useTranslation("camera-mapping");
  const { data: currentUser } = useCurrentUser();
  const isStaff = currentUser?.isStaff ?? false;
  // 체크박스형 2차원 필터 — 각 차원에서 보고 싶은 상태들을 자유 조합
  const [includeAnalysis, setIncludeAnalysis] = useState(true);
  const [includeExcludeAnalysis, setIncludeExcludeAnalysis] = useState(true);
  const [includeShown, setIncludeShown] = useState(true);
  const [includeNotShown, setIncludeNotShown] = useState(true);
  // F10: 카메라 변경 상태 (레거시 space-identify 필터 그대로) — additionId/adjustmentId 기준
  const [includeNotChanged, setIncludeNotChanged] = useState(true);
  const [includeAdjusted, setIncludeAdjusted] = useState(true);
  const [includeAdded, setIncludeAdded] = useState(true);
  // F10: 이벤트 소스 타입 — 도면에 배치된(floorPlanConfig) 라인/구역 기준
  const [includeLines, setIncludeLines] = useState(true);
  const [includeZones, setIncludeZones] = useState(true);
  const [includeNoEventSource, setIncludeNoEventSource] = useState(true);
  const cardGridRef = useRef<HTMLDivElement>(null);
  const [memoOpen, setMemoOpen] = useState(false);
  const [allMemosOpen, setAllMemosOpen] = useState(false);
  const [localPreviewUrls] = useState<Map<number, string>>(new Map());
  // MemoPanel mount 시 자동 add 모드로 진입할지. toolbar "메모 추가" 신호 받으면 true,
  // 카드 메모 아이콘(조회) 으로 열 땐 false. (PO QA 3차 w-3)
  const [addOnNextMemoOpen, setAddOnNextMemoOpen] = useState(false);

  // 외부 시그널(마커 툴바 메모 추가 등)로 메모 패널 열기
  useEffect(() => {
    if (openMemoSignal != null && openMemoSignal > 0) {
      setMemoOpen(true);
      setAllMemosOpen(false);
      setAddOnNextMemoOpen(true);
    }
  }, [openMemoSignal]);

  // 도면에서 메모 마커 클릭 시 메모 패널 자동 열기
  const prevSelectedMemoIdRef = useRef(selectedMemoId);
  useEffect(() => {
    const prev = prevSelectedMemoIdRef.current;
    prevSelectedMemoIdRef.current = selectedMemoId;
    if (selectedMemoId != null) {
      setMemoOpen(true);
    } else if (prev != null) {
      // 메모 선택이 해제되었을 때 (카메라 클릭 등) 메모 패널 닫기
      setMemoOpen(false);
    }
  }, [selectedMemoId]);

  // Memo count per camera
  const memoCountMap = useMemo(() => {
    const map = new Map<number, number>();
    for (const memo of memos) {
      map.set(memo.cameraId, (map.get(memo.cameraId) ?? 0) + 1);
    }
    return map;
  }, [memos]);

  const getDisplayName = useCallback((camera: Camera) => {
    return camera.analysisNumber ?? nonAnalysisNumberMap.get(camera.id) ?? camera.defaultCamId;
  }, [nonAnalysisNumberMap]);

  const handleMemoClickFromCard = useCallback((camera: Camera) => {
    onCameraSelect(camera.id);
    setAllMemosOpen(false);
    setMemoOpen(true);
    // 카드 메모 아이콘 = 조회 의도. toolbar add 신호로 set 됐던 flag 가 남아 있으면 cleanup
    setAddOnNextMemoOpen(false);
  }, [onCameraSelect]);

  const handleAllMemoClick = useCallback((cameraId: number) => {
    onCameraSelect(cameraId);
  }, [onCameraSelect]);

  // 어느 도면에든 매핑돼 있는지 — "도면 표시/미표시" 필터의 기준
  const isOnAnyFloorPlan = useCallback(
    (c: Camera) => someFloorPlanCameraIds.has(c.id),
    [someFloorPlanCameraIds],
  );

  // 2차원 체크박스 필터 — 각 차원에서 보고 싶은 상태들의 합집합. AND 로 결합
  const matchAnalysisDim = useCallback(
    (c: Camera) => {
      const analyzed = isCameraAnalyzed(c.analysisNumber);
      return analyzed ? includeAnalysis : includeExcludeAnalysis;
    },
    [includeAnalysis, includeExcludeAnalysis],
  );
  const matchFloorPlanDim = useCallback(
    (c: Camera) => {
      const shown = isOnAnyFloorPlan(c);
      return shown ? includeShown : includeNotShown;
    },
    [includeShown, includeNotShown, isOnAnyFloorPlan],
  );

  // F10: 카메라 변경 상태 (레거시 filterByAdditionOrAdjustmentStatus 동일):
  //  변경없음 = additionId/adjustmentId 둘 다 없음 / 조정 = adjustmentId 있음 / 추가 = additionId 있음 (OR)
  const matchChangeStatusDim = useCallback(
    (c: Camera) => {
      if (includeNotChanged && c.additionId == null && c.adjustmentId == null) return true;
      if (includeAdjusted && c.adjustmentId != null) return true;
      if (includeAdded && c.additionId != null) return true;
      return false;
    },
    [includeNotChanged, includeAdjusted, includeAdded],
  );

  // F10: 이벤트 소스 타입 (레거시 filterByEventSourceType 동일) — 도면 배치된(floorPlanConfig) 라인/구역 기준
  const matchEventSourceDim = useCallback(
    (c: Camera) => {
      const cfg = configByCameraId.get(c.id);
      const lines = cfg?.countingLines ?? [];
      const zones = cfg?.intervalZones ?? [];
      if (includeLines && lines.some((l) => l.floorPlanConfig != null)) return true;
      if (includeZones && zones.some((z) => z.floorPlanConfig != null)) return true;
      if (
        includeNoEventSource &&
        lines.every((l) => l.floorPlanConfig == null) &&
        zones.every((z) => z.floorPlanConfig == null)
      ) {
        return true;
      }
      return false;
    },
    [configByCameraId, includeLines, includeZones, includeNoEventSource],
  );

  // 카운트는 절대값 — 다른 체크박스 토글 시 숫자가 변하지 않음
  const hasFloorPlanLine = useCallback(
    (c: Camera) => (configByCameraId.get(c.id)?.countingLines ?? []).some((l) => l.floorPlanConfig != null),
    [configByCameraId],
  );
  const hasFloorPlanZone = useCallback(
    (c: Camera) => (configByCameraId.get(c.id)?.intervalZones ?? []).some((z) => z.floorPlanConfig != null),
    [configByCameraId],
  );
  const filterCounts = useMemo(() => ({
    analysis: cameras.filter((c) => isCameraAnalyzed(c.analysisNumber)).length,
    excludeAnalysis: cameras.filter((c) => !isCameraAnalyzed(c.analysisNumber)).length,
    shown: cameras.filter((c) => isOnAnyFloorPlan(c)).length,
    noFloorPlan: cameras.filter((c) => !isOnAnyFloorPlan(c)).length,
    notChanged: cameras.filter((c) => c.additionId == null && c.adjustmentId == null).length,
    adjusted: cameras.filter((c) => c.adjustmentId != null).length,
    added: cameras.filter((c) => c.additionId != null).length,
    lines: cameras.filter((c) => hasFloorPlanLine(c)).length,
    zones: cameras.filter((c) => hasFloorPlanZone(c)).length,
    noEventSource: cameras.filter((c) => !hasFloorPlanLine(c) && !hasFloorPlanZone(c)).length,
  }), [cameras, isOnAnyFloorPlan, hasFloorPlanLine, hasFloorPlanZone]);

  const filteredCameras = useMemo(() => {
    const result = cameras.filter(
      (c) => matchAnalysisDim(c) && matchFloorPlanDim(c) && matchChangeStatusDim(c) && matchEventSourceDim(c),
    );
    // 표시 번호 기준 정렬 (지하 b3→b1 → 지상 1→n / N1,N2,N3 는 뒤)
    return result.sort((a, b) =>
      compareCamId(
        a.analysisNumber ?? nonAnalysisNumberMap.get(a.id) ?? "",
        b.analysisNumber ?? nonAnalysisNumberMap.get(b.id) ?? "",
      ),
    );
  }, [cameras, matchAnalysisDim, matchFloorPlanDim, matchChangeStatusDim, matchEventSourceDim, nonAnalysisNumberMap]);

  // F9: 목록 필터 결과를 부모로 통지 → 도면 캔버스에도 같은 카메라만 표시.
  // 모든 차원이 전체 선택(필터 미적용)이면 null 을 보내 도면이 전체를 그대로 그리게 한다.
  const isFilteringActive =
    !(includeAnalysis && includeExcludeAnalysis) ||
    !(includeShown && includeNotShown) ||
    !(includeNotChanged && includeAdjusted && includeAdded) ||
    !(includeLines && includeZones && includeNoEventSource);
  useEffect(() => {
    onVisibleCameraIdsChange?.(
      isFilteringActive ? new Set(filteredCameras.map((c) => c.id)) : null,
    );
  }, [filteredCameras, isFilteringActive, onVisibleCameraIdsChange]);

  // 도면별 그룹핑 — localMappings 우선, 없으면 서버의 floorPlanId 사용
  const groupedCameras = useMemo(() => {
    const getEffectiveFpId = (c: Camera): number | null => {
      if (!someFloorPlanCameraIds.has(c.id)) return null;
      const local = localMappings.get(c.id);
      return local ? local.floorPlanId : c.floorPlanId;
    };
    const buckets = new Map<number | null, Camera[]>();
    for (const cam of filteredCameras) {
      const fpId = getEffectiveFpId(cam);
      const arr = buckets.get(fpId);
      if (arr) arr.push(cam);
      else buckets.set(fpId, [cam]);
    }
    const groups: { floorPlan: FloorPlan | null; cameras: Camera[] }[] = [];
    const sorted = [...floorPlans].sort((a, b) => a.floorNumber - b.floorNumber);
    for (const fp of sorted) {
      const cams = buckets.get(fp.id);
      if (cams?.length) groups.push({ floorPlan: fp, cameras: cams });
    }
    const unmapped = buckets.get(null);
    if (unmapped?.length) groups.push({ floorPlan: null, cameras: unmapped });
    return groups;
  }, [filteredCameras, floorPlans, localMappings, someFloorPlanCameraIds]);

  // 스크롤 요청: "bottom" = 맨 아래, number = 해당 카메라 ID의 카드로 스크롤
  const pendingScrollRef = useRef<"bottom" | number | null>(null);

  // 도면에서 카메라 클릭 시 해당 카메라가 현재 필터에 없으면 적절한 탭으로 전환 + 스크롤
  const prevSelectedCameraIdRef = useRef(selectedCameraId);
  useEffect(() => {
    const changed = selectedCameraId !== prevSelectedCameraIdRef.current;
    prevSelectedCameraIdRef.current = selectedCameraId;
    if (!changed || selectedCameraId == null) return;

    const camera = cameras.find((c) => c.id === selectedCameraId);
    if (!camera) return;

    pendingScrollRef.current = selectedCameraId;

    const isInCurrentFilter = filteredCameras.some((c) => c.id === selectedCameraId);
    if (isInCurrentFilter) {
      // 현재 필터에 있으면 바로 스크롤
      requestAnimationFrame(() => {
        if (pendingScrollRef.current === selectedCameraId) {
          pendingScrollRef.current = null;
          scrollToCard(cardGridRef.current, selectedCameraId);
        }
      });
    } else {
      // 카메라가 현재 필터 밖이면 모든 체크박스를 ON 으로 풀어 보이게 한다
      setIncludeAnalysis(true);
      setIncludeExcludeAnalysis(true);
      setIncludeShown(true);
      setIncludeNotShown(true);
    }
  }, [selectedCameraId, cameras, filteredCameras]);

  // scrollToBottom 시그널이 바뀌면 모든 체크박스 ON + 맨 아래 스크롤 예약
  useEffect(() => {
    if (scrollToBottom == null) return;
    pendingScrollRef.current = "bottom";
    setIncludeAnalysis(true);
    setIncludeExcludeAnalysis(true);
    setIncludeShown(true);
    setIncludeNotShown(true);
  }, [scrollToBottom]);

  // 필터 전환 후 filteredCameras가 갱신되면 예약된 스크롤 실행
  useEffect(() => {
    const target = pendingScrollRef.current;
    if (target == null) return;
    // 특정 카메라 스크롤은 해당 카메라가 현재 필터에 있을 때만 실행
    if (typeof target === "number" && !filteredCameras.some((c) => c.id === target)) return;
    pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      if (target === "bottom") {
        cardGridRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth", block: "end" });
      } else {
        scrollToCard(cardGridRef.current, target);
      }
    });
  }, [filteredCameras]);

  const selectedCamera = useMemo(
    () => cameras.find((c) => c.id === selectedCameraId) ?? null,
    [cameras, selectedCameraId],
  );

  const selectedCameraDisplayName = selectedCamera
    ? (selectedCamera.analysisNumber ?? nonAnalysisNumberMap.get(selectedCamera.id) ?? selectedCamera.defaultCamId)
    : null;

  // F7: 추가 카메라(원본 목록에 없던 수동 추가)면 새 메모 기본 타입을 '추가(조정/추가)'로
  const isSelectedCameraAdd =
    selectedCameraId != null && cameraMappingTypes.get(selectedCameraId) === "add";

  const selectedImageUrl = selectedCamera
    ? (localPreviewUrls.get(selectedCamera.id) ?? selectedCamera.previewImageUrl ?? selectedCamera.originalImageUrl)
    : undefined;

  // 미리보기 영역을 원본 이미지 비율에 맞춘다 — 16:9 고정 박스에 cover 로 채우면
  // 정사각형/4:3 카메라 스냅샷의 위아래가 잘려나가기 때문 (조정안 작성 시 화각 확인 불가).
  // 세로로 과하게 길어지지 않도록 정사각형(1) ~ 파노라마(21:9) 사이로만 제한.
  const [previewRatio, setPreviewRatio] = useState<number | null>(null);
  const previewImgRef = useRef<HTMLImageElement>(null);

  const applyPreviewRatio = useCallback((img: HTMLImageElement | null) => {
    if (!img?.naturalWidth || !img.naturalHeight) return;
    setPreviewRatio(Math.max(img.naturalWidth / img.naturalHeight, PREVIEW_MIN_RATIO));
  }, []);

  // 카메라를 바꾸면 새 이미지 로드 전까지는 기본 비율로. 단, 이미 캐시된 이미지는
  // onLoad 가 안 오는 경우가 있어 complete 여부로 즉시 반영한다.
  useEffect(() => {
    const img = previewImgRef.current;
    if (img?.complete) applyPreviewRatio(img);
    else setPreviewRatio(null);
  }, [selectedImageUrl, applyPreviewRatio]);

  const handlePreviewImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => applyPreviewRatio(e.currentTarget),
    [applyPreviewRatio],
  );

  return (
    <Container>
      {isAdjustmentActiveForSelected && selectedCamera && (
        <AdjustmentBanner>
          <AdjustmentBannerLabel>
            <Icon icon="ic_chart_default" width={14} height={14} />
            <span>{t("adjustment.activeBanner")}</span>
          </AdjustmentBannerLabel>
          <AdjustmentBannerActions>
            <AdjustmentBannerSecondary
              type="button"
              onClick={() => onCancelAdjustment?.(selectedCamera.id)}
            >
              {t("adjustment.remove")}
            </AdjustmentBannerSecondary>
            <AdjustmentBannerPrimary
              type="button"
              onClick={() => onEndAdjustment?.()}
            >
              {t("adjustment.end")}
            </AdjustmentBannerPrimary>
          </AdjustmentBannerActions>
        </AdjustmentBanner>
      )}
      <PreviewSection>
        <PreviewImageArea
          $highlight={!!isAdjustmentActiveForSelected}
          $ratio={previewRatio ?? PREVIEW_BASE_RATIO}
        >
          <SnapshotInner $inset={selectedCameraAdjustmentFrame != null}>
            <PreviewImage
              ref={previewImgRef}
              src={selectedImageUrl || TEMP_IMAGE_URL}
              alt={selectedCamera?.defaultCamId}
              onLoad={handlePreviewImageLoad}
            />
            {selectedCamera && selectedCameraAdjustmentFrame != null && (
              <AdjustmentHelperRect
                frame={selectedCameraAdjustmentFrame}
                interactive={!!isAdjustmentActiveForSelected}
                onChange={(frame) => onAdjustmentFrameChange?.(selectedCamera.id, frame)}
                onChangeStart={onAdjustmentFrameChangeStart}
              />
            )}
          </SnapshotInner>
          {selectedCamera && (
            <ImageActions>
              <ImageActionButton $active={memoOpen} onClick={() => setMemoOpen((prev) => !prev)}>
                <Icon icon="ic_chart_description" width={16} height={16} />
              </ImageActionButton>
              <ImageActionButton onClick={() => window.open(selectedImageUrl || TEMP_IMAGE_URL, "_blank")}>
                <Icon icon="ic_fullsize2" width={16} height={16} />
              </ImageActionButton>
            </ImageActions>
          )}
        </PreviewImageArea>
      </PreviewSection>

      <TabsRow>
        <TabsGroup>
          <Tab
            type="button"
            $active={!allMemosOpen}
            onClick={() => {
              setAllMemosOpen(false);
              setMemoOpen(false);
            }}
          >
            {t("tabs.cameras")}
          </Tab>
          <Tab
            type="button"
            $active={allMemosOpen}
            onClick={() => {
              setAllMemosOpen(true);
              setMemoOpen(false);
            }}
          >
            {t("tabs.memos")}
          </Tab>
        </TabsGroup>
        {/* 분석 초안처럼 탭 우측에 필터 + 임시카메라 추가 */}
        <TabsRowRight>
          <FilterPopover
            flags={{
              includeAnalysis, includeExcludeAnalysis, includeShown, includeNotShown,
              includeNotChanged, includeAdjusted, includeAdded,
              includeLines, includeZones, includeNoEventSource,
            }}
            counts={filterCounts}
            filteredCount={filteredCameras.length}
            onApply={(next) => {
              setIncludeAnalysis(next.includeAnalysis);
              setIncludeExcludeAnalysis(next.includeExcludeAnalysis);
              setIncludeShown(next.includeShown);
              setIncludeNotShown(next.includeNotShown);
              setIncludeNotChanged(next.includeNotChanged);
              setIncludeAdjusted(next.includeAdjusted);
              setIncludeAdded(next.includeAdded);
              setIncludeLines(next.includeLines);
              setIncludeZones(next.includeZones);
              setIncludeNoEventSource(next.includeNoEventSource);
            }}
          />
          {/* QA 3-1: 고객사는 임시 카메라 자원 신규 생성 ❌ — 버튼 미노출 */}
          {isStaff && (
            <TempCameraButton
              variant="contained"
              color="primary"
              size="xSmall"
              onClick={onAddTempCamera}
            >
              {t("addTempCamera")}
            </TempCameraButton>
          )}
        </TabsRowRight>
      </TabsRow>

      {allMemosOpen ? (
        <AllMemosPanel
          memos={memos}
          cameras={cameras}
          getDisplayName={getDisplayName}
          selectedCameraId={selectedCameraId}
          selectedMemoId={selectedMemoId}
          isPlacedOnContext={(memo) =>
            memo.floorPlanId != null
            && memo.floorPlanId === selectedFloorPlanId
            && memo.positionForFloorPlan != null
          }
          onSave={onMemoSave}
          onClose={() => setAllMemosOpen(false)}
          onMemoClick={handleAllMemoClick}
          onMemoSelect={onMemoSelect}
          onToggleMemoPlacement={onToggleMemoOnFloorPlan}
          showHeader={false}
        />
      ) : memoOpen ? (
        <MemoPanel
          memos={memos}
          selectedCameraId={selectedCameraId}
          cameraDisplayName={selectedCameraDisplayName}
          selectedFloorPlanId={selectedFloorPlanId}
          selectedMemoId={selectedMemoId}
          addOnMount={addOnNextMemoOpen}
          onConsumeAddOnMount={() => setAddOnNextMemoOpen(false)}
          defaultNewMemoType={isSelectedCameraAdd ? "adjustmentOrAddition" : "default"}
          onMemoSelect={onMemoSelect}
          onSave={onMemoSave}
          onClose={() => setMemoOpen(false)}
          onToggleMemoOnFloorPlan={onToggleMemoOnFloorPlan}
        />
      ) : (
        <CardListSection>
          <CameraListWrapper>
            <ScrollArea><CameraListInner>
              {filteredCameras.length > 0 ? (
                <CardGrid ref={cardGridRef}>
                  {groupedCameras.length <= 1
                    ? filteredCameras.map((camera) => (
                        <MappingCameraCard
                          key={camera.id}
                          camera={camera}
                          state={camera.id === selectedCameraId ? "selected" : "default"}
                          localMappingType={cameraMappingTypes.get(camera.id) ?? null}
                          displayNumber={camera.analysisNumber ?? nonAnalysisNumberMap.get(camera.id) ?? null}
                          memoCount={memoCountMap.get(camera.id) ?? 0}
                          overrideImageUrl={localPreviewUrls.get(camera.id)}
                          onClick={onCameraClick}
                          onMemoClick={handleMemoClickFromCard}
                        />
                      ))
                    : groupedCameras.map(({ floorPlan, cameras: groupCameras }) => (
                        <FloorPlanGroup key={floorPlan?.id ?? "unmapped"}>
                          <FloorPlanGroupHeader>
                            <Icon icon="ic_map" width={14} height={14} />
                            <FloorPlanGroupName>
                              {floorPlan?.name ?? t("group.unmapped")}
                            </FloorPlanGroupName>
                            <CameraCountBadge>{groupCameras.length}</CameraCountBadge>
                          </FloorPlanGroupHeader>
                          {groupCameras.map((camera) => (
                            <MappingCameraCard
                              key={camera.id}
                              camera={camera}
                              state={camera.id === selectedCameraId ? "selected" : "default"}
                              localMappingType={cameraMappingTypes.get(camera.id) ?? null}
                              displayNumber={camera.analysisNumber ?? nonAnalysisNumberMap.get(camera.id) ?? null}
                              memoCount={memoCountMap.get(camera.id) ?? 0}
                              overrideImageUrl={localPreviewUrls.get(camera.id)}
                              onClick={onCameraClick}
                              onMemoClick={handleMemoClickFromCard}
                            />
                          ))}
                        </FloorPlanGroup>
                      ))
                  }
                </CardGrid>
              ) : (
                <EmptyMessage>{t("empty")}</EmptyMessage>
              )}
            </CameraListInner></ScrollArea>
          </CameraListWrapper>
        </CardListSection>
      )}
    </Container>
  );
}

// --- Styles ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px 8px 24px;
  border-bottom: 1px solid rgba(143, 149, 178, 0.24);
`;

// 원본 비율을 유지하되 세로 공간은 16:9 때와 동일하게 묶는다 —
// 16:9 보다 좁은(정사각형 등) 스냅샷은 높이를 고정하고 폭이 줄어든다 → 목록이 밀리지 않음
const PreviewImageArea = styled.div<{ $highlight?: boolean; $ratio: number }>`
  position: relative;
  aspect-ratio: ${({ $ratio }) => $ratio};
  /* 부모(flex column)의 stretch 를 끄지 않으면 폭이 강제로 늘어나 비율이 깨진다 */
  align-self: center;
  max-width: 100%;
  ${({ $ratio }) =>
    $ratio >= PREVIEW_BASE_RATIO
      ? css`
          width: 100%;
        `
      : css`
          height: ${PREVIEW_HEIGHT}px;
        `}
  border-radius: 4px;
  border: ${({ $highlight }) =>
    $highlight ? "2px solid #484BD2" : "1px solid rgba(143, 149, 178, 0.32)"};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${token("colors.gray.GR50")};
`;

// 화각 조정이 있는 경우에만 inset (70%) — helper rect 평행이동 시 외곽 여백 안에서 움직임 보임 (legacy 동일).
// 조정 없을 땐 100% 풀사이즈로 표시
const SnapshotInner = styled.div<{ $inset?: boolean }>`
  position: relative;
  width: ${({ $inset }) => ($inset ? "70%" : "100%")};
  height: ${({ $inset }) => ($inset ? "70%" : "100%")};
  transition: width 0.15s ease, height 0.15s ease;
`;

const AdjustmentBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 16px 8px 24px;
  background: rgba(72, 75, 210, 0.08);
  border-bottom: 1px solid rgba(72, 75, 210, 0.24);
  flex-shrink: 0;
`;

const AdjustmentBannerLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  color: #484BD2;
`;

const AdjustmentBannerActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const AdjustmentBannerSecondary = styled.button`
  height: 26px;
  padding: 0 10px;
  border: 1px solid rgba(72, 75, 210, 0.4);
  border-radius: 6px;
  background: transparent;
  color: #484BD2;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    background: rgba(72, 75, 210, 0.08);
  }
`;

const AdjustmentBannerPrimary = styled.button`
  height: 26px;
  padding: 0 10px;
  border: 1px solid #484BD2;
  border-radius: 6px;
  background: #484BD2;
  color: #fff;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    background: #313E83;
    border-color: #313E83;
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  /* 원본 비율 보존 — 박스 비율이 원본과 같으므로 보통 여백 없이 꽉 차고,
     비율 제한(1 ~ 21:9)에 걸린 경우에만 여백이 생긴다 */
  object-fit: contain;
`;

const ImageActions = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ImageActionButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 3px;
  border: none;
  background: rgba(17, 17, 31, 0.32);
  color: #fff;
  cursor: pointer;

  ${({ $active }) =>
    $active &&
    css`
      background: #11111F;
    `}

  &:hover {
    background: rgba(17, 17, 31, 0.48);
  }
`;

const CardListSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px 10px 24px;
  flex-shrink: 0;
`;

const TabsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TabsRowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 필터 트리거(28px)와 높이 맞춤
const TempCameraButton = styled(Button)`
  && {
    height: 28px;
    min-height: 28px;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 6px 0;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? token("colors.text.primary") : "transparent")};
  background: transparent;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  line-height: 1.4;
  color: ${({ $active }) => ($active ? token("colors.text.primary") : token("colors.gray.GR600"))};
  cursor: pointer;

  &:hover {
    color: ${token("colors.text.primary")};
  }
`;

const CameraListWrapper = styled.div`
  flex: 1;
  min-height: 0;
`;

const CameraListInner = styled.div`
  padding: 0 12px 100px 24px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const FloorPlanGroup = styled.section`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  /* 도면/미배치 그룹 경계 구분 (F6) — 첫 그룹 위엔 선 없음.
     컨테이너 좌우 패딩(좌24·우12)만큼 음수 마진으로 끌어내 divider 를 full-bleed,
     같은 만큼 패딩으로 내용(헤더·카드)은 제자리 유지 */
  &:not(:first-child) {
    border-top: 1px solid rgba(143, 149, 178, 0.24);
    margin: 4px -12px 0 -24px;
    padding: 16px 12px 0 24px;
  }
`;

const FloorPlanGroupHeader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  color: ${token("colors.text.secondary")};
`;

const FloorPlanGroupName = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
`;

const CameraCountBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${token("colors.text.secondary")};
  background: rgba(143, 149, 178, 0.16);
  border-radius: 10px;
  padding: 2px 6px;
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 12px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;

